import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

import ClearFilterBtn from "../../../application/ClearFilterBtn";

import Highcharts from 'highcharts/highstock';

import useDataSource from "../../../../hooks/useDataSource";
import useMainCanvasSize from "../../../../hooks/useMainCanvasSize";
import useWindowSize from "../../../../hooks/useWindowSize";

import { filterUpdateConditionAction } from "../../../../redux/data/dataActions";

import {
    getDataColor
} from "../../../../utilities/ColorUtilities";

import {
    compareValues,
    sortDates
} from "../../../../utilities/DataUtilities";

import {
    getFieldAlias,
    getFieldFormatters,
    areLoadDependenciesMet
} from "../../../../utilities/DataSourceUtilities";

import {
    formatDate,
    formatNumber,
    formatStyle,
    formatSuffix,
    formatText
} from "../../../../utilities/FormatUtilities";

import {
    dataAddFilter,
    dataRemoveFilter
} from "../../../../redux/data/dataActions";
import SettingsForm from "../settings/SettingsForm";
import {appSetModalContent} from "../../../../redux/app/appActions";
import _ from "lodash";
import {findComponent} from "../../../../redux/page/pageActions";

import { styled, Card, CardHeader, CardContent, LinearProgress } from '@mui/material';

const PREFIX = 'BarChart';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    cardHeader: `${PREFIX}-cardHeader`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: 'calc(100% - 1px)'
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.cardHeader}`]: {
        padding: '8px 8px 0px 8px',
        alignItems: 'start',
        textAlign: 'left',
        textTransform: 'none',
    }
}));

export default function BarChart({id, configuration, xId}) {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    // App State
    const config = useSelector(state => state.config);
    const persist = useSelector(state => state.persist);
    const filters = useSelector(state => state.data.filters);
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

    // Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
    const [chart, setChart] = useState(null);
    const [previousClientHeight, setPreviousClientHeight] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [sliceValue, setSliceValue] = useState(-2112);
    const [sliceGroupedValue, setSliceGroupedValue] = useState(-2112);
    const [sliceValueNotFound, setSliceValueNotFound] = useState(false);
    const [noDataMessage, setNoDataMessage] = useState("No Data");
    const [categoryFieldFormatters, setCategoryFieldFormatters] = useState(null);
    const [countFieldAlias, setCountFieldAlias] = useState(null);
    const [countFieldFormatters, setCountFieldFormatters] = useState(null);
    const [valueFieldAlias, setValueFieldAlias] = useState(null);
    const [valueFieldFormatters, setValueFieldFormatters] = useState(null);
    const [showProgress, setShowProgress] = useState(compConfig.element?.showFetching !== undefined ? compConfig.element?.showFetching : true);
    const [groupData, setGroupData] = useState(false);
    const [stackedData, setStackedData] = useState(false);
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});
	const [resizedComponent, setResizedComponent] = useState(false);

	const orientation = compConfig.element?.orientation ? compConfig.element?.orientation : null
	const sortType = compConfig.element?.sortType ? compConfig.element?.sortType : null
	const grouped = compConfig.element?.grouped ? compConfig.element?.grouped : null
	const stacked = compConfig.element?.stacked ? compConfig.element?.stacked : null
	const customColors = compConfig?.element?.colors;
	const styles = compConfig?.element?.styles;
	const highchartOptions = compConfig?.highchartOptions ? compConfig?.highchartOptions : {};
	const dataSourceName = compConfig.dataSource?.name ? compConfig.dataSource?.name : '';
	const dependencies = compConfig.dataSource?.dependencies ? compConfig.dataSource?.dependencies : null;
	const categoryField = compConfig.dataSource?.fields?.categoryField ? compConfig.dataSource?.fields?.categoryField : '';
	const valueField = compConfig.dataSource?.fields?.valueField ? compConfig.dataSource?.fields?.valueField : '';
	const filterField = compConfig.xFilter?.field ? compConfig.xFilter?.field : null;
	const filterByValue = compConfig.xFilter?.byValue !== undefined ? compConfig.xFilter?.byValue : false;
	const filterOperator = compConfig.xFilter?.operator ? compConfig.xFilter?.operator : null;

	const chartRef = useRef(null);
	const dataSource = useDataSource(dataSourceName);
	const size = useMainCanvasSize();
	const windowSize = useWindowSize();


	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

	// componentUpdated & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
				setResizedComponent(true);
			}
		}
	}, [componentUpdated]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'Bar Chart Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'BarChart'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

    useEffect(() => {

        //console.log('-----------------BAR CHART---------STATUS->');
        // console.log('id: {0}'.format(id));
        // console.log('fetching: {0}'.format(dataSource.fetching));
        // console.log('fetched: {0}'.format(dataSource.fetched));
        // console.log('error: {0}'.format(dataSource.error));
        // console.log('data:', dataSource.data);
        // console.log('chartData:', chartData);

        if (dataSource.error) {
            setNoDataMessage("Error");
            setChartData([]);
        }
        else if (dataSource.data && dataSource.fetched) {

            // If our dependencies are not met then we're
            // loading/no chance until they are
            if (!areLoadDependenciesMet(dependencies, filters)) {
                return;
            }

            setNoDataMessage("No Data");

            // Setup base data as filtered results or a baseline if included
            let data = dataSource.data;
            // console.log('------------DATA----------------->', data);

            if (dataSource.includeBaselineResults && dataSource.baselines.length > 0) {
                // Find my baseline
                const baseline = dataSource.baselines.find(d => {
                    return d.id === id
                });

                // console.log('------------BASELINE------------->', baseline);
                if (baseline) {
                    let useBaseline = true;

                    // If there is a baseline then we use
                    // that value IF we can highlight
                    if (sliceValue !== -2112) {
                        if (!baseline.result.find(d => {
                                    if (d[categoryField] && sliceValue) {
                                        return d[categoryField].toString().toLowerCase() === sliceValue.toString().toLowerCase();
                                    } else {
                                        return false;
                                    }
                                }
                            )
                        ) {
                            useBaseline = false;
                        }
                    }

                    // console.log('------------------USE BASELINE---------------->', useBaseline);
                    if (useBaseline) {
                        data = baseline.result;
                    }
                }
            }

            // Rehydrate slice
            const componentFilters = filters.find(f => {
                return f.id === id && f.field === categoryField
            });

            // Get formatter for value
            // const valueFormatter = getFieldFormatters(dataSource, 'sum(budget) as value');
            // console.log('{0}'.format('Value formatter...'));
            // console.log(valueFormatter);

            // TODO: Add Else If for groupData where setSliceValue === sliceGroupValue
            // This will reduce usage of sliceGroupValue
            if (componentFilters && !groupData) {
                // TODO: Add utility or store in state since this
                // only assumes single condition - for now we'll use
                // local context however sharing across components
                // TBD will share same f.id
                // HACK:
                let condition = componentFilters.conditions[0];
                if (condition.indexOf("eq '") === 0) {
                    // String eq
                    condition = condition.substr(condition.indexOf("eq '") + 4);
                    condition = condition.substr(0, condition.length - 1);

                    setSliceValue(condition.toLowerCase());
                } else if (condition.indexOf("eq ") === 0) {
                    // Numeric eq
                    condition = condition.substr(condition.indexOf("eq ") + 3);

                    setSliceValue(condition.toLowerCase());
                } else if (condition.indexOf("like '") === 0) {
                    // String like
                    condition = condition.substr(condition.indexOf("like '") + 6);
                    condition = condition.substr(0, condition.length - 1);

                    if (condition.length > 2) {
                        if (condition.substr(0, 1) === '%') {
                            condition = condition.substr(1);
                        }
                        if (condition.substr(condition.length -1) === '%') {
                            condition = condition.substr(0, condition.length -1);
                        }
                    }

                    // console.log(condition);

                    setSliceValue(condition.toLowerCase());
                } else {
                    console.log("**WARN** BarChart '{0}' was not a recognized filter condition".format(condition));
                }
            }

            if (!chart) {
                // Aliases and formatters - using for tooltips
                // setCategoryFieldAlias(getFieldAlias(dataSource, categoryField));
                setCategoryFieldFormatters(getFieldFormatters(dataSource, categoryField));
                setCountFieldAlias(getFieldAlias(dataSource, valueField !== 'count' ? valueField : 'count'));
                setCountFieldFormatters(getFieldFormatters(dataSource, valueField !== 'count' ? valueField : 'count'));
                setValueFieldAlias(getFieldAlias(dataSource, 'value'));
                setValueFieldFormatters(getFieldFormatters(dataSource, 'value'));

                setGroupData (grouped !== null ? (grouped.groupDataSet ? true : false) : false);
                setStackedData (stacked !== null ? (stacked.stackedDataSet ? true : false) : false);
            }

            // Update Data/Chart
            setChartData(data);
        }
    }, [dataSource.data, dataSource.fetching, dataSource.fetched, dataSource.error]);

    useEffect(() => {

        updateChart();

    }, [chartData, sliceValue, persist.themeLight, t]);

    useEffect(() => {
        const componentFilters = filters.find(f => {
            return f.id === id && f.field === categoryField
        });

        if (!componentFilters && ((sliceValue !== -2112) || (sliceGroupedValue !== -2112))) {
            setSliceValue(-2112);
            setSliceGroupedValue(-2112);
        }
    }, [filters]);

    useEffect( () => {
        // if (chart) {
        //     // setTimeout required to accurately obtain chart.renderTo.clientHeight and chart.renderTo.clientWidth to perform the reflow
        //     setTimeout(function() {
        //         // Stacked Charts are rebuilt, and highlighting Stacked Columns needs done once chart.series has been built
        //         // if (!stackedData) {
        //         //     console.log('BC: reflow');
        //             chart.reflow();
        //         // }
        //     }, 300)
        // }
        if (chart) {
            // Determine if we have crossed threshold and need to redo
            // underlying data i.e. slices etc.

            // setTimeout required to accurately obtain chart.renderTo.clientHeight and chart.renderTo.clientWidth to perform the reflow
            setTimeout(function() {
                chart.reflow();
				setResizedComponent(resizedComponent === true ? false : false);
            }, 300)
        }
    }, [size, windowSize, resizedComponent, cardHeaderStyle, cardContentStyle])

    const getSeriesColor = (index, value, sliceValueToCompare) => {
        // Example ramping values
        if (sliceValueToCompare === null || sliceValueToCompare == -2112) {
            // There are no selected values so we just want to ramp the colors
            return getDataColor(index, value, config.data.colorMap);
        } else {
            // We have selected values so we want not selected or the ramp color
            if (value === sliceValueToCompare) {
                return getDataColor(index, value, config.data.colorMap);
            } else {
                return persist.themeLight ? '#767676' : '#c0c2c9';
            }
        }
    }

    const formatTooltip = (formatters, label, input) => {

        // console.log('{0}'.format('--> BarChart: formatTooltip()'));
        // console.log('FORMATTERS: ', formatters);
        // console.log('LABEL: {0} ({1})'.format(label, typeof label));
        // console.log('INPUT: {0} ({1})'.format(input, typeof input));

        let formattedValue = input, style = "", tooltip;
        if (formatters) {
            for (const formatter of formatters) {
                if (formatter.type.toLowerCase() === 'style') {
                    style = formatStyle(formattedValue, formatter.config, input, formatter.condition, true);
                } else if (formatter.type.toLowerCase() === 'number') {
                    formattedValue = formatNumber(formattedValue, formatter.config);
                } else if (formatter.type.toLowerCase() === 'date') {
                    formattedValue = formatDate(formattedValue, formatter.config);
                } else if (formatter.type.toLowerCase() === 'test') {
                    formattedValue = formatText(formattedValue, formatter.config);
                }
            }
        }

        if (label) {
            tooltip = "<tr><td style='text-align:left'>" + label + ":</td><td style='text-align:right'><span style=" + style + ">" + formattedValue + "</td></tr>";
        } else {
            tooltip = "<tr><td colspan='2' style='" + style + "'>" + formattedValue + "</td></tr>";
        }

        // console.log('TOOLTIP: {0} ({1})'.format(tooltip, typeof tooltip));
        return tooltip;
    }

    const clickEvent = (event) => {
        // NOTE: You can look up other values (assuming you have an ID) by
        // filtering the input array
        let filterFieldToAdd;
        let filterValueToAdd;

        if (!filterByValue) {
            filterFieldToAdd = categoryField;
            filterValueToAdd = event.point.category;
        } else {
            filterFieldToAdd = filterField;
            filterValueToAdd = event.point.options.y;
        }

        if (sliceValue.toString().toLowerCase() === filterValueToAdd.toString().toLowerCase()) {
            dispatch(dataRemoveFilter(id));
        }
        else {
            if (filterOperator.toLowerCase() === 'like') {
                dispatch(dataAddFilter(id, filterFieldToAdd, " like '%{0}%'".format(filterValueToAdd), filterUpdateConditionAction.REPLACE, true, false));
            } else {
                dispatch(dataAddFilter(id, filterFieldToAdd, " eq '{0}'".format(filterValueToAdd), filterUpdateConditionAction.REPLACE, true, false));
            }
        }
    }

    const clickGroupedEvent = (event) => {
        // NOTE: You can look up other values (assuming you have an ID) by
        // filtering the input array
        let filterFieldToAdd = categoryField;
        let filterValueToAdd = event.point.category;
        let groupFilterValue = event.point.category + '-' + event.point.series.name;

        if (sliceGroupedValue.toString().toLowerCase() === groupFilterValue.toString().toLowerCase()) {
            dispatch(dataRemoveFilter(id));
        }
        else {
            if (filterOperator.toLowerCase() === 'like') {
                dispatch(dataAddFilter(id, filterFieldToAdd, " like '%{0}%'".format(filterValueToAdd), filterUpdateConditionAction.REPLACE, true, false));
            } else {
                dispatch(dataAddFilter(id, filterFieldToAdd, " eq '{0}'".format(filterValueToAdd), filterUpdateConditionAction.REPLACE, true, false));
            }
            setSliceGroupedValue(groupFilterValue);
        }
    }

    const updateChart = () => {

        let data = chartData;

        if (!chart && (data === null))
            return;

        // NOTE: Removed the length condition because 'No Data' wasn't being displayed
        // if (!chart && (data === null || data.length === 0))
        //     return;

        if (chart && data === null) {
            // No Data
            chart.update({
                series: [{
                    name: '{0}'.format(categoryField),
                    data: []
                }]
            }, true, true);
            chart.showNoData();
            return;
        }

        // Sort the data to match the sorted Categories
        if (data !== null) {
            data = sortType !== null ? (sortType.toLowerCase() === 'date' ? data.sort(sortDates(categoryField)) : data.sort(compareValues(categoryField))) : data.sort(compareValues(categoryField));
        }
        // console.log('DATA---------------------------->>>>', data);

        // Setup categories
        let categories = [];
        categories = [...new Set(data.map(n => (n[categoryField])))];
        // Remove NULL categories
        categories = categories.filter(n => {
            return n !== null;
        });
        // Sort categories
        categories = sortType !== null ? (sortType.toLowerCase() === 'date' ? categories.sort(sortDates(categoryField)) : categories.sort(compareValues(categoryField))) : categories.sort(compareValues(categoryField));
        // console.log('CATEGORIES---------------------->>>>', categories);

        // Setup groups
        let groups = [];
        let groupedColors = new Array(0);
        if (groupData) {
            if (grouped.groupedField) {
                groups = [...new Set(data.map(n => (n[grouped.groupedField])))];
                // Sort groups
                groups = groups.sort();
            } else {
                groups = grouped.groupedManyFields;
                // Sort groups
                groups = groups.sort();
            }
            groupedColors = grouped.groupedColors;
        }
        // console.log(groupedColors);
        // console.log('GROUPS-------------------------->>>>', groups);

        // Setup stacks
        let stacks = [];
        let stackColors = [];
        if (stackedData) {
            stacks = stacked.stackedFields;
            stackColors = stacked.stackedColors;
            // stacks = [...new Set(data.map(n => (n[stacked.stackedFields])))];
            // Sort stacks
            // stacks = stacks.sort();
        }
        // console.log('STACKS-------------------------->>>>', stacks);

        let sliceValueToCompare = sliceValue.toString().toLowerCase();
        // console.log('SLICEVALUETOCOMPARE---------------->>>>', sliceValueToCompare);

        // Setup series
        let series = [];
        let sliceFound = false;

        if (groupData) {
            sliceValueToCompare = sliceGroupedValue.toString().toLowerCase();
            // console.log('SLICEGROUPEDVALUE------------------>>>>', sliceValueToCompare);

            let dataSet = [];
            if (grouped.groupedField) {
                dataSet = groups.map((g, gIndex) => {
                    return {
                        name: g,
                        data: categories.map((c, cIndex) => {
                            if (c.toLowerCase() + '-' + g.toLowerCase() === sliceValueToCompare) {
                                sliceFound = true;
                            }
                            return {
                                y: data.reduce((acc, val) => {
                                    return ((val[categoryField] === c) && (val[grouped.groupedField] === g)) ? acc + val[valueField] : acc;
                                }, 0),
                                color: groupedColors !== undefined ? (sliceValueToCompare === '-2112' ? getDataColor(gIndex, groups[gIndex].toLowerCase(), groupedColors) : getSeriesColor(gIndex, c.toLowerCase() + '-' + groups[gIndex].toLowerCase(), sliceValueToCompare.toLowerCase())) : getSeriesColor(gIndex, groups[gIndex].toLowerCase(), sliceValueToCompare.toLowerCase()),
                                borderWidth: c.toLowerCase() + '-' + g.toLowerCase() === sliceValueToCompare ? config.data.highlight.borderWidth : 0,
                                borderColor: c.toLowerCase() + '-' + g.toLowerCase() === sliceValueToCompare ? config.data.highlight.borderColor : 0,
                                events: {
                                    click: function (event) {
                                        clickGroupedEvent(event);
                                    }
                                }
                            }
                        }),
                        animation: chart ? ({
                            duration: 0
                        }) : {}
                    }
                })
            } else {
                dataSet = groups.map((g, gIndex) => {
                    return {
                        name: g,
                        data: categories.map((c, cIndex) => {
                            // console.log(c);
                            if (c.toLowerCase() + '-' + g.toLowerCase() === sliceValueToCompare) {
                                sliceFound = true;
                            }
                            return {
                                y: data.reduce((acc, val) => {
                                    let dataVal;
                                    for (let property in val) {
                                        if (g === property) {
                                            dataVal = (val[categoryField] === c) ? acc + val[g] : acc;
                                            break;
                                        } else {
                                            dataVal = acc;
                                        }
                                    }
                                    return dataVal;
                                }, 0),
                                color: groupedColors !== undefined ? (sliceValueToCompare === '-2112' ? getDataColor(gIndex, groups[gIndex].toLowerCase(), groupedColors) : getSeriesColor(gIndex, c.toLowerCase() + '-' + groups[gIndex].toLowerCase(), sliceValueToCompare.toLowerCase())) : getSeriesColor(gIndex, groups[gIndex].toLowerCase(), sliceValueToCompare.toLowerCase()),
                                borderWidth: c.toLowerCase() + '-' + g.toLowerCase() === sliceValueToCompare ? config.data.highlight.borderWidth : 0,
                                borderColor: c.toLowerCase() + '-' + g.toLowerCase() === sliceValueToCompare ? config.data.highlight.borderColor : 0,
                                events: {
                                    click: function (event) {
                                        clickGroupedEvent(event);
                                    }
                                }
                            }
                        }),
                        animation: chart ? ({
                            duration: 0
                        }) : {}
                    }
                })
            }
            series = dataSet;
        } else if (stackedData) {
            let dataSet = [];
            if (stacked?.dynamicStackedFields !== undefined && stacked?.dynamicStackedFields.enabled === true) {

                // Create the various Dynamic Series Names
                const dynamicStackNames = data.map(d => d[stacked.dynamicStackedFields.dynamicStackedCategoryField])
                    .filter((v, i, a) => a.indexOf(v) == i)
                    .sort();

                dataSet = dynamicStackNames.map((s, sIndex) => {

                    let values = [];
                    for (let j = 0; j < categories.length; j++) {
                        values.push(data.reduce((acc, val) => {
                                return (s === val[stacked.dynamicStackedFields.dynamicStackedCategoryField] && val[categoryField] === categories[j])
                                    ? acc + (val[stacked.dynamicStackedFields.dynamicStackedValueField] ? val[stacked.dynamicStackedFields.dynamicStackedValueField] : 0)
                                    : acc;
                            }, 0)
                        );
                        // console.log(values);
                    }
                    // return values
                    const labelAlias = getFieldAlias(dataSource, s);
                    const label = labelAlias !== null ? labelAlias : s;

                    // if the dataset value is null, just assign it null as a string and move on
                    // this avoids a .toLowerCase() function error
                    if (s === null) {
                        s = 'null';
                    }
                    return {
                        name: label,
                        data: values,
                        color: stackColors.length > 0 ? (sliceValueToCompare === '-2112' ? getDataColor(sIndex, s.toLowerCase(), stackColors) : getSeriesColor(sIndex, s.toLowerCase(), sliceValueToCompare.toLowerCase())) : getSeriesColor(sIndex, s.toLowerCase(), sliceValueToCompare.toLowerCase()),
                        events: {
                            click: function (event) {
                                clickEvent(event);
                            }
                        }
                    }
                })
            } else {
                dataSet = stacks.map((s, sIndex) => {

                    // Need to compare properties with case differences
                    let values = [];
                    values = data.map((d, dIndex) => {
                        let dataVals = []
                        for (let property in d) {

                            // Check if Sliced by comparing the Filter Field value to the Clicked/Sliced value
                            if (property.toLowerCase() === filterField.toLowerCase()) {
                                if (d[property] !== null) {
                                    if (d[property].toLowerCase() === sliceValueToCompare.toLowerCase()) {
                                        sliceFound = true;
                                    }
                                }
                            }

                            // Does Data Field (lowercase) match Stack Field (lowercase)?
                            if (property.toLowerCase() === s.toLowerCase()) {
                                dataVals.push(d[property]);
                            }
                        }
                        return dataVals
                    });
                    const labelAlias = getFieldAlias(dataSource, s);
                    const label = labelAlias !== null ? labelAlias : s;
                    return {
                        name: label,
                        data: values,
                        color: stackColors.length > 0 ? (sliceValueToCompare === '-2112' ? getDataColor(sIndex, s.toLowerCase(), stackColors) : getSeriesColor(sIndex, s.toLowerCase(), sliceValueToCompare.toLowerCase())) : getSeriesColor(sIndex, s.toLowerCase(), sliceValueToCompare.toLowerCase()),
                        events: {
                            click: function (event) {
                                clickEvent(event);
                            }
                        }
                    }
                })
            }
            series = dataSet;
        } else {
            for (let d = 0; d < data.length; d++) {
                const item = data[d];

                let value = item[categoryField];
                if (!value)
                    value = "";
                else
                    value = value.toString().toLowerCase();

                if (value === sliceValueToCompare) {
                    sliceFound = true;
                }

                // We do not want NULL Categories Values, so do not add to dataset
                if (item[categoryField] !== null && item[valueField] !== null) {
                    series.push({
                        name: t(item[categoryField]),
                        y: item[valueField],
                        value: item.value,
                        // color: getSeriesColor(d, value, sliceValueToCompare), //getDataColor(d, value, config.data.colorMap)
                        color: customColors !== undefined ? (sliceValueToCompare === '-2112' ? getDataColor(d, value, customColors) : getSeriesColor(d, value, sliceValueToCompare)) : getSeriesColor(d, value, sliceValueToCompare),
                        borderWidth: value === sliceValueToCompare ? config.data.highlight.borderWidth : 0,
                        borderColor: value === sliceValueToCompare ? config.data.highlight.borderColor : 0
                    });
                }
            }
        }
        // console.log('SERIES-------------------------->>>>', series);

        // NOTE: If we don't find the active slice what do we want to do??
        // HACK: TEMP: Add the missing slice??
        if (!sliceFound && sliceValueToCompare !== '-2112') {
            series.push({
                name: sliceValue.toString().toUpperCase(),
                y: 0.12345,
                value: undefined,
                color: 'red',
                borderWidth: config.data.highlight.borderWidth,
                borderColor: config.data.highlight.borderColor,
            });
            categories.push(sliceValue.toString().toUpperCase());
            setSliceValueNotFound(true);
        } else {
            setSliceValueNotFound(false);
        }
		// console.log('SERIES-------------------------->>>>', series);


        if (!chart) {

            const chartOptions = {
                chart: {
                    type: orientation === 'horizontal' ? 'bar' : (orientation === 'vertical' ? 'column' : (orientation === 'area' ? 'area' : (orientation === 'line' ? 'line' : 'bar'))),
                    backgroundColor: null,
                    height: highchartOptions?.height ? highchartOptions.height : null
                },
                credits: {
                    enabled: false
                },
                // Card Attribution Used
                title: {
                    text: '',
                    margin: 0
                },
                subtitle: {
                    text: '',
                    margin: 0
                },
                lang: {
                    noData: noDataMessage
                },
                noData: {
                    style: {
                        fontWeight: 'bold',
                        fontSize: '15px',
                        color: '#303030'
                    }
                },
                xAxis: {
                    tickInterval: highchartOptions.xAxis.tickInterval ? highchartOptions.xAxis.tickInterval : null,
                    //type: 'category',
                    categories: categories,
                    // crosshair: true,
                    title: {
                        text: t(highchartOptions.xAxis.title.text) ? t(highchartOptions.xAxis.title.text) : "",
                        style: {
                            color: persist.themeLight ? '#767676' : '#ffffff',
                        }
                    },
                    labels: {
                        ...(highchartOptions.xAxis.labels ? highchartOptions.xAxis.labels : {}),
                        style: {
                            color: persist.themeLight ? '#767676' : '#ffffff',
                        },
                        useHTML: highchartOptions.xAxis.useHTML ? highchartOptions.xAxis.useHTML : true,
                        formatter: function() {
                            // console.log('--> xAxis.labels.formatter()');
                            if (highchartOptions.xAxis.labels.hasOwnProperty('formatters')) {
                                let formattedValue = this.value;
                                for (const formatter of highchartOptions.xAxis.labels.formatters) {
                                    if (formatter.type.toLowerCase() === 'date') {
                                        formattedValue = formatDate(formattedValue, formatter.config)
                                    } else if (formatter.type.toLowerCase() === 'number') {
                                        formattedValue = formatNumber(formattedValue, formatter.config);
                                        // } else if (formatter.type.toLowerCase() === 'style') {
                                        //     formattedValue = formatStyle(formattedValue, formatter.config);
                                    } else if (formatter.type.toLowerCase() === 'text') {
                                        formattedValue = formatText(formattedValue, formatter.config);
                                    } else if (formatter.type.toLowerCase() === 'suffix') {
                                        formattedValue = formatSuffix(formattedValue, formatter.config);
                                    }
                                }
                                return t(formattedValue);
                            } else {
                                return t(this.value);
                            }
                        }
                    },
                    plotLines: [
                        ...(highchartOptions.xAxis.plotLines ? highchartOptions.xAxis.plotLines : {})
                    ]
                },
                yAxis: {
                    tickInterval: highchartOptions.yAxis.tickInterval ? highchartOptions.yAxis.tickInterval : null,
                    max: highchartOptions.yAxis.max ? highchartOptions.yAxis.max : null,
                    title: {
                        text: t(highchartOptions.yAxis.title.text) ? t(highchartOptions.yAxis.title.text) : "",
                        style: {
                            color: persist.themeLight ? '#767676' : '#ffffff',
                        }
                    },
                    labels: {
                        ...(highchartOptions.yAxis.labels ? highchartOptions.yAxis.labels : {}),
                        style: {
                            color: persist.themeLight ? '#767676' : '#ffffff',
                        },
                        useHTML: highchartOptions.yAxis.useHTML ? highchartOptions.yAxis.useHTML : true,
                        formatter: function() {
                            // console.log('--> yAxis.labels.formatter()');
                            if (highchartOptions.yAxis.labels.hasOwnProperty('formatters')) {
                                let formattedValue = this.value;
                                for (const formatter of highchartOptions.yAxis.labels.formatters) {
                                    if (formatter.type.toLowerCase() === 'date') {
                                        formattedValue = formatDate(formattedValue, formatter.config)
                                    } else if (formatter.type.toLowerCase() === 'number') {
                                        formattedValue = formatNumber(formattedValue, formatter.config);
                                        // } else if (formatter.type.toLowerCase() === 'style') {
                                        //     formattedValue = formatStyle(formattedValue, formatter.config);
                                    } else if (formatter.type.toLowerCase() === 'text') {
                                        formattedValue = formatText(formattedValue, formatter.config);
                                    } else if (formatter.type.toLowerCase() === 'suffix') {
                                        formattedValue = formatSuffix(formattedValue, formatter.config);
                                    }
                                }
                                return t(formattedValue);
                            } else {
                                return t(this.value);
                            }
                        }
                    },
                    plotLines: [
                        ...(highchartOptions.yAxis.plotLines ? highchartOptions.yAxis.plotLines : {})
                    ]
                },
                legend:{
                    ...(highchartOptions.legend ? highchartOptions.legend : {}),
                    itemStyle: {
                        color: persist.themeLight ? '#767676' : '#ffffff',
                    },
                    labelFormatter: function () {
                        let label;
                        if (orientation === 'vertical' || orientation === 'horizontal') {
                            if (groupData || stackedData) {
                                const labelAlias = getFieldAlias(dataSource, this.name);
                                label = labelAlias !== null ? labelAlias : this.name;
                            } else {
                                const labelAlias = getFieldAlias(dataSource, this.data.category);
                                label = labelAlias !== null ? labelAlias : this.data.category;
                            }
                        } else {
                            const labelAlias = getFieldAlias(dataSource, this.name);
                            label = labelAlias !== null ? labelAlias : this.name;
                        }

                        return t(label);
                    }
                },
                tooltip: {
                    ...(highchartOptions.tooltip ? highchartOptions.tooltip : {}),
                    shared: false,
                    borderColor: "#000000",
                    useHTML: true,
                    //followPointer: true,
                    formatter: function () {
                        if (this.y === 0.12345) {
                            let formattedTooltip = "<table>"
                            formattedTooltip += formatTooltip(categoryFieldFormatters, null, t(this.point.category));
                            formattedTooltip += "<tr><td>No Data Available</td></tr>";
                            formattedTooltip += "</table>";
                            return formattedTooltip;
                        } else {
                            let input = this.point.category;
                            //let inputAsDate = input instanceof Date ? input : (input.toString().indexOf(':') >= 0 ? new Date(input) : new Date(input+'T00:00'));
                            let inputAsDate = '';
                            // Check if Conversion failed...possible fail if adding the UTC time
                            if (isNaN(input)) {
                                inputAsDate = new Date(input);
                            } else {
								if (typeof input !== 'number') {
									input = t(this.point.category);
								} else {
									input = this.point.category;
								}
                            }
                            let formattedTooltip = "<table>";
                            formattedTooltip += formatTooltip(categoryFieldFormatters, null, input);
                            //formattedTooltip += formatTooltip(categoryFieldFormatters, null, t(this.point.category));
                            if (groupData || stackedData) {
                                const label = getFieldAlias(dataSource, this.series.name);
                                formattedTooltip += formatTooltip(
                                    getFieldFormatters(dataSource, this.series.name),
                                    t(label !== null ? label : this.series.name),
                                    this.y
                                );
                            } else {
                                formattedTooltip += formatTooltip(countFieldFormatters, t(countFieldAlias), this.y);
                                // formattedTooltip += formatTooltip(countFieldFormatters, (groupData || stackedData ? t(this.series.name) : t(countFieldAlias)), this.y);
                            }
                            formattedTooltip += (typeof this.point.value !== 'undefined') ? formatTooltip(valueFieldFormatters, t(valueFieldAlias), this.point.value) : ''; // if undefined, assume there is no Value to show
                            formattedTooltip += "</table>";
                            return formattedTooltip;
                        }
                    }
                },
                plotOptions: {
                    column: {
                        ...highchartOptions.plotOptions.column,
                        minPointLength: 0
                    },
                    series: {
                        ...highchartOptions.plotOptions.series,
                        dataLabels: {
                            formatter: function() {
                                if (this.y == 0) {
                                    return "";
                                }else{
                                    return this.y;
                                }
                            }
                        }
                    }
                },
                series: groupData || stackedData ? series : [{
                    name: "",
                    colorByPoint: true,
                    data: series,
                    events: {
                        click: function(event) {
                            clickEvent(event);
                        }
                    }
                }]
            };
            // console.log('BarChart: Create', chartOptions);

            setChart(Highcharts.chart(chartRef.current, chartOptions));
        } else {

            // console.log('BarChart: Update');

            chart.update({
                // NOTE: We need to update our handlers otherwise they will point to
                // a previously defined function with a context that does not match
                // the current state - to reproduce comment out plot options
                // filter once chart, clear, then filter the 2nd chart and all
                // previously applied filters will be displayed
                xAxis: {
                    tickInterval: highchartOptions.xAxis.tickInterval ? highchartOptions.xAxis.tickInterval : null,
                    //type: 'category',
                    categories: categories,
                    // crosshair: true,
                    title: {
                        text: t(highchartOptions.xAxis.title.text) ? t(highchartOptions.xAxis.title.text) : "",
                        style: {
                            color: persist.themeLight ? '#767676' : '#ffffff',
                        }
                    },
                    labels: {
                        ...(highchartOptions.xAxis.labels ? highchartOptions.xAxis.labels : {}),
                        style: {
                            color: persist.themeLight ? '#767676' : '#ffffff',
                        },
                        useHTML: highchartOptions.xAxis.useHTML ? highchartOptions.xAxis.useHTML : true,
                        formatter: function() {
                            // console.log('--> xAxis.labels.formatter()');
                            if (highchartOptions.xAxis.labels.hasOwnProperty('formatters')) {
                                let formattedValue = this.value;
                                for (const formatter of highchartOptions.xAxis.labels.formatters) {
                                    if (formatter.type.toLowerCase() === 'date') {
                                        formattedValue = formatDate(formattedValue, formatter.config)
                                    } else if (formatter.type.toLowerCase() === 'number') {
                                        formattedValue = formatNumber(formattedValue, formatter.config);
                                        // } else if (formatter.type.toLowerCase() === 'style') {
                                        //     formattedValue = formatStyle(formattedValue, formatter.config);
                                    } else if (formatter.type.toLowerCase() === 'text') {
                                        formattedValue = formatText(formattedValue, formatter.config);
                                    } else if (formatter.type.toLowerCase() === 'suffix') {
                                        formattedValue = formatSuffix(formattedValue, formatter.config);
                                    }
                                }
                                return t(formattedValue);
                            } else {
                                return t(this.value);
                            }
                        }
                    },
                    plotLines: [
                        ...(highchartOptions.xAxis.plotLines ? highchartOptions.xAxis.plotLines : {}),
                    ]
                },
                yAxis: {
                    tickInterval: highchartOptions.yAxis.tickInterval ? highchartOptions.yAxis.tickInterval : null,
                    max: highchartOptions.yAxis.max ? highchartOptions.yAxis.max : null,
                    title: {
                        text: t(highchartOptions.yAxis.title.text) ? t(highchartOptions.yAxis.title.text) : "",
                        style: {
                            color: persist.themeLight ? '#767676' : '#ffffff',
                        }
                    },
                    labels: {
                        ...(t(highchartOptions.yAxis.labels) ? t(highchartOptions.yAxis.labels) : {}),
                        style: {
                            color: persist.themeLight ? '#767676' : '#ffffff',
                        },
                        useHTML: highchartOptions.yAxis.useHTML ? highchartOptions.yAxis.useHTML : true,
                        formatter: function() {
                            // console.log('--> yAxis.labels.formatter()');
                            if (highchartOptions.yAxis.labels.hasOwnProperty('formatters')) {
                                let formattedValue = this.value;
                                for (const formatter of highchartOptions.yAxis.labels.formatters) {
                                    if (formatter.type.toLowerCase() === 'date') {
                                        formattedValue = formatDate(formattedValue, formatter.config)
                                    } else if (formatter.type.toLowerCase() === 'number') {
                                        formattedValue = formatNumber(formattedValue, formatter.config);
                                        // } else if (formatter.type.toLowerCase() === 'style') {
                                        //     formattedValue = formatStyle(formattedValue, formatter.config);
                                    } else if (formatter.type.toLowerCase() === 'text') {
                                        formattedValue = formatText(formattedValue, formatter.config);
                                    } else if (formatter.type.toLowerCase() === 'suffix') {
                                        formattedValue = formatSuffix(formattedValue, formatter.config);
                                    }
                                }
                                return t(formattedValue);
                            } else {
                                return t(this.value);
                            }
                        }
                    },
                    plotLines: [
                        ...(highchartOptions.yAxis.plotLines ? highchartOptions.yAxis.plotLines : {})
                    ]
                },
                legend:{
                    ...(highchartOptions.legend ? highchartOptions.legend : {}),
                    itemStyle: {
                        color: persist.themeLight ? '#767676' : '#ffffff',
                    },
                    labelFormatter: function () {
                        let label;
                        if (orientation === 'vertical' || orientation === 'horizontal') {
                            if (groupData || stackedData) {
                                const labelAlias = getFieldAlias(dataSource, this.name);
                                label = labelAlias !== null ? labelAlias : this.name;
                            } else {
                                const labelAlias = getFieldAlias(dataSource, this.data.category);
                                label = labelAlias !== null ? labelAlias : this.data.category;
                            }
                        } else {
                            const labelAlias = getFieldAlias(dataSource, this.name);
                            label = labelAlias !== null ? labelAlias : this.name;
                        }

                        return t(label);
                    }
                },
                tooltip: {
                    ...(highchartOptions.tooltip ? highchartOptions.tooltip : {}),
                    shared: false,
                    borderColor: "#000000",
                    useHTML: true,
                    //followPointer: true,
                    formatter: function () {
                        if (this.y === 0.12345) {
                            let formattedTooltip = "<table>"
                            formattedTooltip += formatTooltip(categoryFieldFormatters, null, t(this.point.category));
                            formattedTooltip += "<tr><td>No Data Available</td></tr>";
                            formattedTooltip += "</table>";
                            return formattedTooltip;
                        } else {
                            let input = this.point.category;
                            //let inputAsDate = input instanceof Date ? input : (input.toString().indexOf(':') >= 0 ? new Date(input) : new Date(input+'T00:00'));
                            let inputAsDate = '';
                            // Check if Conversion failed...possible fail if adding the UTC time
                            if (isNaN(input)) {
                                inputAsDate = new Date(input);
                            } else {
								if (typeof input !== 'number') {
									input = t(this.point.category);
								} else {
									input = this.point.category;
								}
                            }
                            let formattedTooltip = "<table>";
                            formattedTooltip += formatTooltip(categoryFieldFormatters, null, input);
                            if (groupData || stackedData) {
                                const label = getFieldAlias(dataSource, this.series.name);
                                formattedTooltip += formatTooltip(
                                    getFieldFormatters(dataSource, this.series.name),
                                    t(label !== null ? label : this.series.name),
                                    this.y
                                );
                            } else {
                                formattedTooltip += formatTooltip(countFieldFormatters, t(countFieldAlias), this.y);
                                // formattedTooltip += formatTooltip(countFieldFormatters, (groupData || stackedData ? t(this.series.name) : t(countFieldAlias)), this.y);
                            }
                            formattedTooltip += (typeof this.point.value !== 'undefined') ? formatTooltip(valueFieldFormatters, t(valueFieldAlias), this.point.value) : ''; // if undefined, assume there is no Value to show
                            formattedTooltip += "</table>";
                            return formattedTooltip;
                        }
                    }
                },
                plotOptions: {
                    column: {
                        ...highchartOptions.plotOptions.column,
                        minPointLength: 0
                    },
                    series: {
                        ...highchartOptions.plotOptions.series,
                        dataLabels: {
                            formatter: function() {
                                if (this.y == 0) {
                                    return "";
                                }else{
                                    return this.y;
                                }
                            }
                        }
                    }
                },
                series: groupData || stackedData ? series : [{
                    name: "",
                    colorByPoint: true,
                    data: series,
                    events: {
                        click: function(event) {
                            clickEvent(event);
                        }
                    }
                }]
            }, false);

            if (groupData || (stackedData && (stacked?.dynamicStackedFields !== undefined && stacked?.dynamicStackedFields.enabled === true ))) {
                // Animation has been removed if chart exists
                // Now Remove old Series and Add new series
                while (chart.series.length > 0) {
                    chart.series[0].remove(false, false);
                }

                for (let i = 0; i < series.length; i++) {
                    chart.addSeries(series[i], false, false);
                }
            } else if (stackedData) {
                const stackColors = stacked.stackedColors;
                const chartSeries = chart.series;
                chartSeries.forEach((series, sIndex) => {
                    series.points.forEach((point, pIndex) => {
                        // On Data Change, points change and do not exist; check they exist!
                        if (point.category !== null) {
                            if (point.category.toLowerCase() === sliceValue) {
                                point.update({
                                    borderColor: config.data.highlight.borderColor,
                                    borderWidth: config.data.highlight.borderWidth,
                                    color: stackColors.length > 0 ? getDataColor(sIndex, series.name, stackColors) : getDataColor(sIndex, null, stackColors)
                                }, false);
                            } else {
                                point.update({
                                    borderColor: 0,
                                    borderWidth: config.data.highlight.borderWidth,
                                    color: stackColors.length > 0 ? (sliceValueToCompare === '-2112' ? getDataColor(sIndex, series.name, stackColors) : getSeriesColor(sIndex, series.name, sliceValueToCompare.toLowerCase())) : getSeriesColor(sIndex, series.name, sliceValueToCompare.toLowerCase())
                                }, false);
                            }
                        }
                    })
                });
            }

            chart.redraw();
         }
    };

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {...styles?.cardContent
			,height: 'calc(100% - 49px)'
			,overflow: 'auto'
			,padding: '0px'}
		);

		if (title && subtitle) {
			// Default Style
		} else if (title) {
			// Title Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
	}

    return (
        <Root className={classes.root}>
            {showProgress && dataSource.fetching &&
                <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !dataSource.fetching &&
                <div className={classes.linearProgress}></div>
            }
            <Card style={{height: '100%', boxShadow: 'none', ...styles?.card}}>
                <CardHeader action={ClearFilterBtn(id, sliceValueNotFound)} className={classes.cardHeader} style={cardHeaderStyle}
                            titleTypographyProps={{color: 'textPrimary', variant: 'subtitle2'}}
                            title={t(title)}
                            subheaderTypographyProps={{color: 'textSecondary', variant: 'body2'}}
                            subheader={t(subtitle)}>
                </CardHeader>
                <CardContent style={cardContentStyle}>
                    <div id={"bccomp"} ref={chartRef} style={{position: 'relative', top: '0px', width: '100%', height: '100%'}}></div>
                </CardContent>
            </Card>
        </Root>
    );
}
